import { ISOString } from "../typings/types/utils";

export const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const pathParamsToQuery = <T extends Record<string, string | undefined>>(
  params?: T
) => {
  if (!params) return '';

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, encodeURIComponent(value));
    } else {
      searchParams.delete(key);
    }
  });

  return `?${searchParams.toString()}`;
};

export const isoTimestamp = (value?: number | string | Date): ISOString => {
  return value
    ? new Date(value).toISOString() as ISOString
    : new Date().toISOString() as ISOString;
};

export function getSessionTokenUid(token: string): string | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);

    const payload = JSON.parse(json);
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

export const objectKeys = <T extends Record<PropertyKey, unknown>>(
  obj: T
): (keyof T)[] => {
  return Object.keys(obj) as (keyof T)[];
};

export type ObjectEntries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

export const objectEntries = <T extends Record<PropertyKey, unknown>>(
  obj: T
): ObjectEntries<T> => {
  return Object.entries(obj) as unknown as ObjectEntries<T>;
};

export function mapQuerySnapshot<T>(snapshot: { docs: any[] }): T[] {
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as T[];
}

export function getResponseHttpStatusCode(err: any): number | undefined {
  if (typeof err !== "object" || err === null) return undefined;
  if (!("response" in err)) return undefined;
  const response = err.response;
  if (typeof response !== "object" || response === null) return undefined;

  if (typeof response.code === "number") return response.code;
  if (typeof response.status === "number") return response.status;
  return undefined;
}
